import type { FC, MouseEvent, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIntro } from '@/components/landing/intro-context'
import { useBooking } from '@/components/landing/booking-provider'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import Magnetic from '@/components/landing/magnetic'
import { NAV_LINKS } from '@/constants/aerive'

const SiteHeader: FC = (): ReactNode => {
    const { introStarted } = useIntro()
    const { openBooking } = useBooking()
    const { scrollTo, stop, start } = useSmoothScroll()
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const toggleRef = useRef<HTMLButtonElement>(null)

    const closeMenu = useCallback((): void => {
        setMenuOpen(false)
        start()
        toggleRef.current?.focus()
    }, [start])

    const openMenu = (): void => {
        stop()
        setMenuOpen(true)
    }

    useEffect(() => {
        if (!menuOpen) return

        menuRef.current?.querySelector<HTMLElement>('a, button')?.focus()

        const handleKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') closeMenu()
        }

        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [menuOpen, closeMenu])

    const go = (href: string) => (event: MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault()
        if (menuOpen) {
            setMenuOpen(false)
            start()
        }
        scrollTo(href)
    }

    const book = (): void => {
        if (menuOpen) {
            setMenuOpen(false)
            start()
        }
        openBooking()
    }

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-opacity duration-1000',
                    introStarted ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
            >
                <div className='flex h-16 items-center justify-between px-5 md:h-20 md:px-10 lg:px-14'>
                    <nav aria-label='Main' className='hidden items-center gap-9 md:flex'>
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                onClick={go(link.href)}
                                className='group relative py-2 text-[11px] uppercase tracking-[0.24em] text-[var(--header-ink-soft)] transition-colors duration-500 hover:text-[var(--header-ink)] focus-visible:outline-none focus-visible:text-[var(--header-ink)]'
                            >
                                {link.label}
                                <span className='absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-[var(--header-ink)] transition-transform duration-500 group-hover:scale-x-100 group-focus-visible:scale-x-100' />
                            </a>
                        ))}
                    </nav>

                    <span className='md:hidden' />

                    <div className='flex items-center gap-3'>
                        <Magnetic className='hidden md:inline-flex'>
                            <button
                                type='button'
                                onClick={book}
                                className='inline-flex h-11 items-center gap-2 rounded-full border border-[var(--header-line)] px-5 text-[11px] uppercase tracking-[0.22em] text-[var(--header-ink)] transition-colors duration-500 hover:border-champagne hover:bg-champagne hover:text-obsidian focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-champagne'
                            >
                                Book a flight
                                <ArrowUpRight className='size-4' />
                            </button>
                        </Magnetic>

                        <button
                            ref={toggleRef}
                            type='button'
                            onClick={openMenu}
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label='Open menu'
                            className='flex size-11 items-center justify-center rounded-full border border-[var(--header-line)] text-[var(--header-ink)] transition-colors duration-500 md:hidden'
                        >
                            <Menu className='size-5' />
                        </button>
                    </div>
                </div>
            </header>

            <div
                id='mobile-menu'
                ref={menuRef}
                role='dialog'
                aria-modal='true'
                aria-label='Menu'
                hidden={!menuOpen}
                className='fixed inset-0 z-[65] flex-col bg-obsidian px-6 pb-10 pt-5 animate-in fade-in duration-300 md:hidden [&:not([hidden])]:flex'
            >
                <div className='flex items-center justify-between'>
                    <span className='pl-[0.32em] font-heading text-[1.4rem] font-medium tracking-[0.32em] text-champagne'>AERIVE</span>
                    <button
                        type='button'
                        onClick={closeMenu}
                        aria-label='Close menu'
                        className='flex size-11 items-center justify-center rounded-full border border-champagne/30 text-champagne'
                    >
                        <X className='size-5' />
                    </button>
                </div>

                <nav aria-label='Mobile' className='mt-16 flex flex-col gap-2'>
                    {NAV_LINKS.map((link, index) => (
                        <a
                            key={link.href}
                            href={link.href}
                            onClick={go(link.href)}
                            className='flex items-baseline justify-between border-b border-champagne/15 py-4 font-heading text-[2.6rem] font-light leading-none text-ivory'
                        >
                            {link.label}
                            <span className='font-sans text-[11px] tracking-[0.2em] text-smoke'>{`0${index + 1}`}</span>
                        </a>
                    ))}
                </nav>

                <button
                    type='button'
                    onClick={book}
                    className='mt-auto inline-flex h-14 items-center justify-center gap-3 rounded-full bg-champagne text-[12px] uppercase tracking-[0.22em] text-obsidian'
                >
                    Book a flight
                    <ArrowUpRight className='size-4' />
                </button>
            </div>
        </>
    )
}

export default SiteHeader
